import React, { useEffect, useState } from "react";
import Header from "../../../modules/common/components/header/Header";
import { GlobalApplicationState } from "../../../globalApplicationState";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import UserPortalSplashScreen from "modules/common/components/splash-screens/userPortalSplashScreen";
import useDrawerExpanded from "modules/common/hooks/useDrawerExpanded";
import { COMMON_COOKIE_KEYS } from "utils/cookie";
import CookieBanner from "../common/components/CookieBanner";
import Menu from "./components/Menu";
import { Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { appInsightsReactPlugin } from "appInsights";
import { Drawer, useMediaQuery, useTheme } from "@mui/material";
import { withRouter, RouteComponentProps } from "react-router";
import ShortcutsFeedList from "modules/shortcuts/components/feedList/shortcutsFeedList";
import { getShortcutsFeed } from "modules/shortcuts/reducer";
import { MenuItemLabel } from "../common/components/MenuItem";

import "./index.sass";

const DRAWER_WIDTH = 62;
const EXPANDED_DRAWER_WIDTH = 230;
const HEADER_HEIGHT = 48;
const EXPANDED_SHORTCUTS_DRAWER_WIDTH = 330;
const DEFAULT_DRAWER_BOX_SHADOW = "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)";
const DEFAULT_DRAWER_RIGHT_BORDER = "1px solid rgba(0, 0, 0, 0.12)";

const useStyles = makeStyles<Theme, { isSmallAndSmaller: boolean, drawerExpanded: boolean, expandShortcutsList: boolean }>((theme: Theme) =>
    createStyles({
        main: {
            display: "flex",
            maxHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
            minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
            overflow: "hidden",
            position: "relative"
        },
        drawer: ({ isSmallAndSmaller, drawerExpanded }) => ({
            flexShrink: 0,
            position: "relative",
            transition: "width linear 0.2s",
            width: (!isSmallAndSmaller || drawerExpanded)
                ? DRAWER_WIDTH
                : 0,
            "&.expanded": {
                width: isSmallAndSmaller
                    ? "100%"
                    : EXPANDED_DRAWER_WIDTH,
                "& $drawerPaper": {
                    width: isSmallAndSmaller
                        ? "100%"
                        : EXPANDED_DRAWER_WIDTH,
                }
            },
            zIndex: theme.zIndex.drawer + 1
        }),
        drawerPaper: ({ isSmallAndSmaller, drawerExpanded, expandShortcutsList }) => ({
            backgroundColor: theme.palette.background.default,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "visible",
            position: "absolute",
            transition: "width linear 0.2s",
            width: (!isSmallAndSmaller || drawerExpanded)
                ? DRAWER_WIDTH
                : 0,
            boxShadow : expandShortcutsList ? "none" : DEFAULT_DRAWER_BOX_SHADOW,
            borderRight: expandShortcutsList ? "none" : DEFAULT_DRAWER_RIGHT_BORDER
        }),
        content: {
            flexGrow: 1,
            overflow: "auto",
            padding: 12,
            position: "relative",
            zIndex: 0,
            "& .portal-page": {
                margin: -12
            }
        },
        shortcutsDrawerPaper: ({ isSmallAndSmaller, drawerExpanded, expandShortcutsList }) => {
            const currentDrawerWidth = drawerExpanded ? EXPANDED_DRAWER_WIDTH : DRAWER_WIDTH;

            return {
                backgroundColor: theme.palette.background.default,
                left: isSmallAndSmaller ? 0 : currentDrawerWidth,
                width: isSmallAndSmaller ? '100%' : currentDrawerWidth + EXPANDED_SHORTCUTS_DRAWER_WIDTH,
                boxShadow: expandShortcutsList ? DEFAULT_DRAWER_BOX_SHADOW : 'none',
                overflowX: 'clip',
                top: isSmallAndSmaller ? undefined : HEADER_HEIGHT,
                height: isSmallAndSmaller ? '50%' : '95%'
            }
        },
        shortcutsDrawerBackdropRoot: ({ isSmallAndSmaller, drawerExpanded }) => {
            const currentDrawerWidth = drawerExpanded ? EXPANDED_DRAWER_WIDTH : DRAWER_WIDTH;

            return {
                backgroundColor: isSmallAndSmaller ? undefined : 'initial',
                left: isSmallAndSmaller ? 0 : currentDrawerWidth + EXPANDED_SHORTCUTS_DRAWER_WIDTH
            }
           
        }
    })
);

const Layout: React.FunctionComponent<PropsWithRedux> = ({
    publicAccess,
    clientName,
    tenant,
    location,
    children
}) => {
    const { drawerExpanded, toggleDrawerExpanded } = useDrawerExpanded(COMMON_COOKIE_KEYS.USER_DRAWER_EXPANDED, true);
    const theme = useTheme();
    const isSmallAndSmaller = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const [expandShortcutsList, setExpandShortcutsList] = useState(false);
    const [lastExpandedState, setLastExpandedState] = useState(drawerExpanded);
    const shortcutsList = useSelector((state: GlobalApplicationState) => state.shortcuts.shortcutFeed);
    const dispatch = useDispatch();
    const classes = useStyles({ isSmallAndSmaller, drawerExpanded, expandShortcutsList });

    useEffect(() => {
        dispatch(getShortcutsFeed());
    }, []);

    const toggleShortcuts = () => {             
        if (expandShortcutsList) {
            setExpandShortcutsList(false);

            if (lastExpandedState !== drawerExpanded && !isSmallAndSmaller)
                toggleDrawerExpanded();
        }
        else {
            setLastExpandedState(drawerExpanded);

            if (drawerExpanded)
                toggleDrawerExpanded();

            setExpandShortcutsList(true);
        }
    }

    const toggleExpand = () => {
        setExpandShortcutsList(false);
        toggleDrawerExpanded();
    }

    return (
        <div id="user-app">
            <Header
                publicAccess={publicAccess}
                onClickMenu={toggleExpand}
                light
                displayName={clientName}
                showRole={false}
                linkToProfile
                headerStyle={{ height: HEADER_HEIGHT }}
                useElevatedHeader
            />
            <div className={classes.main}>
                <nav className={`${classes.drawer} ${drawerExpanded ? "expanded" : ""}`}>
                    <Menu
                        expand={drawerExpanded}
                        locationPathName={location.pathname}
                        publicAccess={publicAccess}
                        toggleExpand={toggleExpand}
                        tenant={tenant.id}
                        drawerPaperClassName={classes.drawerPaper}
                        hideIcons={isSmallAndSmaller && !drawerExpanded}
                        drawerWidth={DRAWER_WIDTH}
                        expandedDrawerWidth={EXPANDED_DRAWER_WIDTH}
                        shortcutsList={shortcutsList}
                        expandShortcutsList={expandShortcutsList}
                        onMenuItemClicked={menuItem => menuItem.children && menuItem.children.length
                            ? setExpandShortcutsList(false) 
                            : (menuItem.label === MenuItemLabel.SHORTCUTS || expandShortcutsList) && toggleShortcuts()
                        }
                    />
                </nav>
                <Drawer
                    open={expandShortcutsList}
                    onClose={() => toggleShortcuts()}
                    variant="temporary"
                    classes={{ paper: classes.shortcutsDrawerPaper }}
                    ModalProps={{ BackdropProps: { classes: { root: classes.shortcutsDrawerBackdropRoot } } }}
                    anchor={ isSmallAndSmaller ? 'bottom' : 'left' }
                >
                    <ShortcutsFeedList shortcutsList={shortcutsList} delayShowListItemsInMs={200} isListShowing={expandShortcutsList}/>
                </Drawer>
                <main className={`${classes.content} main-content`}>
                    {children}
                </main>
            </div>
            <CookieBanner />
            <div id="pause-window" className="pause-window-execution" />
        </div>
    );
}

class UserAppLayout extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props: PropsWithRedux) {
        super(props);

        this.state = {
            displaySplashScreen: false
        };
    }

    public componentDidMount() {
        document.title = this.props.clientName || "Loading...";

        let favicon: any = document.querySelector("link[rel~='icon']");
        if (!!favicon)
            favicon.href = "/favicon.png";

        if (!!this.props.history.location.state && !!this.props.history.location.state.prevPath && this.props.history.location.state.prevPath.includes("/admin/"))
            this.setState({ displaySplashScreen: true });
    }

    public componentDidUpdate(prevProps: PropsWithRedux) {
        if (!!this.props.clientName && !prevProps.clientName)
            document.title = this.props.clientName;
    }

    public render() {
        return (
            <UserPortalSplashScreen displaySplashScreen={this.state.displaySplashScreen}>
                <Layout {...this.props} />
            </UserPortalSplashScreen>
        );
    }
}

interface ComponentProps {
    publicAccess?: boolean;
}

interface ComponentState {
    displaySplashScreen: boolean;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        clientName: state.settings.clientSettings.displayName,
        tenant: state.tenant
    })
);
type PropsWithRedux = ConnectedProps<typeof connector> & RouteComponentProps<{}>;

export default withRouter(connector(withAITracking(appInsightsReactPlugin, UserAppLayout)));
